import { Fragment, ReactNode, useEffect, useMemo, useRef } from "react";
import { Modal } from "antd";
import { MdAgriculture, MdCalendarToday } from "react-icons/md";
import styled from "styled-components";
import { useFilters } from "core/features/filters/store";
import { gray300, gray700, gray900 } from "core/resources/theme/colors";
import { useGetRdoCurvaS } from "../../hooks/useGetRdoCurvaS";
import { useGetRdoRitmoDiario } from "../../hooks/useGetRdoRitmoDiario";
import { SELECT_POPUP_Z_INDEX } from "../../utils";
import { formatHa, formatRdoDate } from "./formatRdo";
import { getRdoOcupacaoIconVisual } from "./getRdoOcupacaoIcon";
import { mapRdoCurvaSToChart } from "./mapRdoCurvaS";
import { mapRdoRitmoDiarioToChart } from "./mapRdoRitmoDiario";
import { buildRdoDetailChartParams } from "./rdoDetailChartParams";
import RdoRhythmChart from "./RdoRhythmChart";
import RdoSCurveChart from "./RdoSCurveChart";
import { RdoTableRow } from "./rdoOperacoes.types";

const TONE = {
  blue: "#2563eb",
  green: "#1f9d55",
  orange: "#e08a1e",
} as const;

type Tone = keyof typeof TONE;

type Props = {
  row: RdoTableRow | null;
  ocupacao: string;
  periodo: string;
  safraPeriodoId: string;
  onClose: () => void;
};

type MetricDef = {
  key: string;
  label: string;
  value: ReactNode;
  unit?: string;
  tone?: Tone;
  exec?: boolean;
  date?: boolean;
};

function contextLabelFor(row: RdoTableRow): string {
  if (row.isParent) return "Classe consolidada";
  const n = row.sequencia != null ? ` ${row.sequencia}` : "";
  return `${row.operacaoCalendarizada ? "Passada" : "Janela"}${n}`;
}

function RdoDetalheOperacaoModal({
  row,
  ocupacao,
  periodo,
  safraPeriodoId,
  onClose,
}: Props) {
  const backRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (row) backRef.current?.focus();
  }, [row?.key]);

  const unidadeId = useFilters((s) => s.filters.unidade?.id);
  const safraId = useFilters((s) => s.filters.safra?.id);

  const chartParams = useMemo(() => {
    if (!row || !unidadeId || !safraId || !safraPeriodoId) return null;
    return buildRdoDetailChartParams({
      unidadeId,
      safraId,
      safraPeriodoId,
      row,
    });
  }, [row, unidadeId, safraId, safraPeriodoId]);

  const open = !!row;
  const { data: curvaSData, isLoading: curvaLoading } = useGetRdoCurvaS({
    params: chartParams,
    enabled: open,
  });
  const { data: ritmoData, isLoading: ritmoLoading } = useGetRdoRitmoDiario({
    params: chartParams,
    enabled: open,
  });

  const curvaPoints = useMemo(
    () => mapRdoCurvaSToChart(curvaSData),
    [curvaSData]
  );
  const ritmoChart = useMemo(
    () => mapRdoRitmoDiarioToChart(ritmoData),
    [ritmoData]
  );

  const iconVisual = getRdoOcupacaoIconVisual(ocupacao, 52);
  const opNome = row?.operacaoNome || row?.nome || "";
  const scurveTitle = `CURVA S - ${opNome} ${periodo || ocupacao}`.trim();
  const pctRaw = row?.percentualExecucao;
  const pct =
    pctRaw == null || Number.isNaN(Number(pctRaw))
      ? null
      : Math.max(0, Math.min(100, Number(pctRaw)));
  const pctStr =
    pct == null ? "—" : `${pct.toFixed(1).replace(".", ",")}%`;
  const contextLabel = row ? contextLabelFor(row) : "";

  const metrics: MetricDef[] = row
    ? [
        {
          key: "planejada",
          label: "ÁREA PLANEJADA",
          value: formatHa(row.areaPrevista),
          unit: "ha",
          tone: "blue",
        },
        {
          key: "realizada",
          label: "ÁREA REALIZADA",
          value: formatHa(row.areaRealizada),
          unit: "ha",
          tone: "green",
        },
        {
          key: "aRealizar",
          label: "ÁREA A REALIZAR",
          value: formatHa(row.areaARealizar),
          unit: "ha",
          tone: "orange",
        },
        {
          key: "exec",
          label: "EXECUÇÃO",
          value: pctStr,
          unit: `${formatHa(row.areaRealizada)} ha de ${formatHa(row.areaPrevista)} ha`,
          tone: "green",
          exec: true,
        },
        {
          key: "meta",
          label: "META DIÁRIA (ha)",
          value: formatHa(row.metaDiaria),
          unit: "ha/dia",
          tone: "blue",
        },
        {
          key: "ritmo",
          label: "RITMO DIÁRIO (ha)",
          value: formatHa(row.ritmoDiario),
          unit: "ha/dia",
          tone: "green",
        },
        {
          key: "metaAj",
          label: "META DIÁRIA AJUSTADA (ha)",
          value: formatHa(row.metaDiariaAjustada),
          unit: "ha/dia",
          tone: "orange",
        },
        {
          key: "inicio",
          label: "DATA INÍCIO",
          value: formatRdoDate(row.dataInicial),
          date: true,
        },
        {
          key: "termino",
          label: "PREVISÃO DE TÉRMINO",
          value: formatRdoDate(row.projecaoTermino),
          date: true,
        },
      ]
    : [];

  return (
    <StyledModal
      visible={!!row}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width="92vw"
      centered
      title={null}
      closable={false}
      zIndex={SELECT_POPUP_Z_INDEX}
    >
      {row ? (
        <Inner>
          <Header>
            <BackBtn
              ref={backRef}
              type="button"
              onClick={onClose}
              aria-label="Voltar"
            >
              ←
            </BackBtn>
            <Titles>
              <h2>Relatório de Detalhamento da Operação</h2>
              <p>Acompanhe o desempenho detalhado da operação selecionada.</p>
            </Titles>
          </Header>

          <Body>
            <KpiStrip>
              <KpiBrandCultura>
                <BrandIcon aria-hidden>{iconVisual.icon}</BrandIcon>
                <BrandText>
                  <BrandLabelTop>CULTURA</BrandLabelTop>
                  <strong>{ocupacao || "—"}</strong>
                </BrandText>
              </KpiBrandCultura>

              <KpiBrandOp>
                <BrandRing aria-hidden>
                  <MdAgriculture size={22} />
                </BrandRing>
                <BrandText $dark>
                  <BrandLabelTop $dark>OPERAÇÃO</BrandLabelTop>
                  <strong>{opNome}</strong>
                  <BrandContext>{contextLabel}</BrandContext>
                </BrandText>
              </KpiBrandOp>

              {metrics.map((m) => (
                <Fragment key={m.key}>
                  <KpiVRule aria-hidden />
                  <KpiMetric $exec={!!m.exec}>
                    <MetricLabel>{m.label}</MetricLabel>
                    <MetricCenter>
                      {m.date ? (
                        <MetricDate>
                          <MdCalendarToday size={14} aria-hidden />
                          {m.value}
                        </MetricDate>
                      ) : (
                        <MetricValue $tone={m.tone}>{m.value}</MetricValue>
                      )}
                      {m.exec ? (
                        <>
                          <ProgressTrack>
                            <ProgressFill
                              style={{ width: `${pct ?? 0}%` }}
                            />
                          </ProgressTrack>
                          <MetricUnit $exec>{m.unit}</MetricUnit>
                        </>
                      ) : m.unit ? (
                        <MetricUnit>{m.unit}</MetricUnit>
                      ) : null}
                    </MetricCenter>
                  </KpiMetric>
                </Fragment>
              ))}
            </KpiStrip>

            <Main>
              <Charts>
                <Panel $kpi>
                  <PanelHead>{scurveTitle}</PanelHead>
                  <ChartSlot>
                    <RdoSCurveChart
                      data={curvaPoints}
                      loading={curvaLoading}
                    />
                  </ChartSlot>
                  <PanelFoot>
                    <LegSwatch $color="#2563eb" /> Planejado
                    <LegSwatch $color="#16a34a" /> Realizado
                    <LegSwatch $color="#f97316" $dash /> Projeção
                  </PanelFoot>
                </Panel>
                <Panel $kpi>
                  <PanelHead>
                    EVOLUÇÃO DO RITMO DIÁRIO <small>(ha/dia)</small>
                  </PanelHead>
                  <ChartSlot>
                    <RdoRhythmChart
                      data={ritmoChart.points}
                      metaDiaria={ritmoChart.metaDiaria}
                      loading={ritmoLoading}
                    />
                  </ChartSlot>
                  <PanelFoot>
                    <LegSwatch $color="#1f9d55" /> ≥ meta
                    <LegSwatch $color="#e08a1e" /> &lt; meta
                    <LegSwatch $color="#2563EB" $dash /> Meta diária
                  </PanelFoot>
                </Panel>
              </Charts>
              <Panel $grow>
                <PanelHead>MAPA DE EXECUÇÃO POR TALHÃO</PanelHead>
                <Placeholder $tall>Sem dados de mapa por talhão</Placeholder>
              </Panel>
            </Main>
          </Body>

          <Footer>
            <span>
              ℹ Dados atualizados em{" "}
              {new Date()
                .toLocaleString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace(",", " às")}
            </span>
            <span style={{ textAlign: "center" }}>
              Projeção de término calculada com base no ritmo médio dos últimos
              3 dias.
            </span>
            <span aria-hidden />
          </Footer>
        </Inner>
      ) : null}
    </StyledModal>
  );
}

export default RdoDetalheOperacaoModal;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0;
    height: 90dvh;
  }
  .ant-modal-content {
    border-radius: 10px;
    overflow: hidden;
  }
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 90dvh;
  margin: 0;
  background: #eef1f4;
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
  padding: 10px 12px;
  background: #fff;
  border-bottom: 1px solid ${gray300};
`;

const Body = styled.div`
  padding: 10px 12px 16px;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Footer = styled.footer`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr 1.4fr 1fr;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 0.75rem;
  color: ${gray700};
  background: #fff;
  border-top: 1px solid ${gray300};
`;

const BackBtn = styled.button`
  border: 1px solid ${gray300};
  background: #fff;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
`;

const Titles = styled.div`
  min-width: 0;
  h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 800;
    color: ${gray900};
  }
  p {
    margin: 2px 0 0;
    font-size: 0.82rem;
    color: ${gray700};
  }
`;

const KpiStrip = styled.section`
  display: flex;
  align-items: stretch;
  gap: 0;
  background: #fff;
  border: 1px solid #dde3ea;
  border-radius: 12px;
  padding: 9px 10px;
  overflow-x: auto;
  min-height: 82px;
  flex-shrink: 0;
`;

const KpiBrandCultura = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #15803d, #166534);
  border-radius: 10px;
  color: #fff;
  padding: 8px 14px 8px 10px;
  margin-right: 4px;
`;

const KpiBrandOp = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 4px 10px 4px 4px;
`;

const BrandIcon = styled.span`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
`;

const BrandRing = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #bbf7d0;
  background: #f0fdf4;
  color: ${TONE.green};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BrandText = styled.div<{ $dark?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;

  strong {
    font-size: ${({ $dark }) => ($dark ? "1rem" : "1.1rem")};
    font-weight: 800;
    color: ${({ $dark }) => ($dark ? "#1f2937" : "inherit")};
  }
`;

const BrandLabelTop = styled.span<{ $dark?: boolean }>`
  font-size: 0.54rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin-bottom: 2px;
  opacity: ${({ $dark }) => ($dark ? 1 : 0.85)};
  color: ${({ $dark }) => ($dark ? "#9ca3af" : "inherit")};
`;

const BrandContext = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: #4b5563;
  margin-top: 3px;
  white-space: nowrap;
`;

const KpiVRule = styled.i`
  width: 1px;
  align-self: stretch;
  background: #e5e7eb;
  margin: 6px 4px;
  flex-shrink: 0;
`;

const KpiMetric = styled.div<{ $exec?: boolean }>`
  flex: 1 1 0;
  min-width: ${({ $exec }) => ($exec ? "120px" : "88px")};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 6px 6px 8px;
  min-height: 90px;
`;

const MetricLabel = styled.span`
  font-size: 0.54rem;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 0.05em;
  line-height: 1.2;
  width: 100%;
  flex-shrink: 0;
`;

const MetricCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
`;

const MetricValue = styled.strong<{ $tone?: Tone }>`
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1.1;
  color: ${({ $tone }) => ($tone ? TONE[$tone] : "#1f2937")};
`;

const MetricUnit = styled.span<{ $exec?: boolean }>`
  font-size: ${({ $exec }) => ($exec ? "0.6rem" : "0.7rem")};
  font-weight: 400;
  color: #9ca3af;
  margin-top: ${({ $exec }) => ($exec ? "4px" : "6px")};
  line-height: 1.2;
`;

const MetricDate = styled.strong`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.86rem;
  font-weight: 800;
  color: #374151;
`;

const ProgressTrack = styled.div`
  width: 100%;
  max-width: 110px;
  height: 5px;
  background: #e8edf2;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 6px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${TONE.green};
  border-radius: 999px;
`;

const MAIN_MIN_H = "560px";

const Main = styled.div`
  flex: 1 0 auto;
  min-height: ${MAIN_MIN_H};
  display: grid;
  grid-template-columns: minmax(260px, 35%) 1fr;
  gap: 12px;
  align-items: stretch;
`;

const Charts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: ${MAIN_MIN_H};
  height: 100%;
`;

const Panel = styled.section<{ $grow?: boolean; $kpi?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #fff;
  border: 1px solid ${({ $kpi }) => ($kpi ? "#dde3ea" : gray300)};
  border-radius: ${({ $kpi }) => ($kpi ? "12px" : "8px")};
  padding: ${({ $kpi }) => ($kpi ? "9px 10px" : "10px")};
  ${({ $grow }) =>
    $grow
      ? `height: 100%; min-height: ${MAIN_MIN_H};`
      : "flex: 1 1 0;"}
`;

const ChartSlot = styled.div`
  flex: 1;
  min-height: 180px;
  overflow: hidden;
  background: #fff;
`;

const PanelHead = styled.h3`
  margin: 0 0 8px;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  color: ${gray900};
  small {
    font-weight: 600;
    color: ${gray700};
  }
`;

const PanelFoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px 12px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #eef1f4;
  font-size: 0.68rem;
  font-weight: 600;
  color: ${gray700};
`;

const LegSwatch = styled.i<{ $color: string; $dash?: boolean }>`
  display: inline-block;
  width: ${({ $dash }) => ($dash ? "16px" : "14px")};
  height: 3px;
  border-radius: 2px;
  background: ${({ $color, $dash }) =>
    $dash ? "transparent" : $color};
  ${({ $color, $dash }) =>
    $dash
      ? `background-image: linear-gradient(to right, ${$color} 60%, transparent 60%); background-size: 6px 3px;`
      : ""}
`;

const Placeholder = styled.div<{ $tall?: boolean }>`
  flex: 1;
  min-height: ${({ $tall }) => ($tall ? "100%" : "220px")};
  border: 1px dashed ${gray300};
  border-radius: 6px;
  display: grid;
  place-items: center;
  color: ${gray700};
  font-size: 0.8rem;
  background: #fafbfc;
`;
